import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { resultadosEmpleados, buscarPorId } from './buscarEmpleados';
import { TarjetaEmpleado } from './TarjetaEmpleado';
import { ExpedienteMobile } from './ExpedienteMobile';

// Empleados Mobile (M.2A / M.2A.1 / M.2B). Herramienta operativa: sólo empleados
// ACTIVOS (las bajas se consultarán en su propia vista). El estado (búsqueda,
// seleccionadoId, seccionExpediente y scroll de cada nivel) vive en MobileShell
// para sobrevivir a la navegación; aquí sólo se presenta. La lista usa los
// `empleados` que App.jsx ya mantiene; el expediente (M.2B) pide su detalle.
export function EmpleadosMobile({ empleados, cambios, loading, estado, setEstado, onAbrir, onAbrirSeccion, onCerrar, guardiaSalida }) {
  const { busqueda, seleccionadoId, seccionExpediente, scrollY, scrollYExpediente } = estado;
  const { lista, totalActivos } = useMemo(
    () => resultadosEmpleados(empleados, busqueda),
    [empleados, busqueda]
  );

  // Al abrir un nivel (expediente o sección) se empieza arriba; al volver se
  // restaura el scroll que tenía el nivel anterior (lista o expediente).
  const nivelPrevio = useRef({ seleccionadoId, seccionExpediente });
  useLayoutEffect(() => {
    const antes = nivelPrevio.current;
    nivelPrevio.current = { seleccionadoId, seccionExpediente };
    let destino = null;
    if (seleccionadoId == null && antes.seleccionadoId != null) destino = scrollY;
    else if (seleccionadoId != null && seccionExpediente == null && antes.seccionExpediente != null && antes.seleccionadoId === seleccionadoId) destino = scrollYExpediente;
    else if (seleccionadoId !== antes.seleccionadoId || seccionExpediente !== antes.seccionExpediente) destino = 0;
    if (destino == null) return;
    try { window.scrollTo(0, destino); } catch { /* sin scroll */ }
  }, [seleccionadoId, seccionExpediente, scrollY, scrollYExpediente]);

  if (seleccionadoId != null) {
    return (
      <ExpedienteMobile
        key={seleccionadoId}
        id={seleccionadoId}
        empleado={buscarPorId(empleados, seleccionadoId)}
        cambios={cambios}
        seccion={seccionExpediente}
        onAbrirSeccion={onAbrirSeccion}
        onVolver={onCerrar}
        guardiaSalida={guardiaSalida}
      />
    );
  }

  const cargando = loading && (!empleados || empleados.length === 0);
  const hayBusqueda = busqueda.trim() !== '';
  const setBusqueda = (valor) => setEstado(prev => ({ ...prev, busqueda: valor }));
  const activosTexto = `${totalActivos} ${totalActivos === 1 ? 'empleado activo' : 'empleados activos'}`;

  return (
    <section className="m-emp" aria-labelledby="m-emp-titulo">
      <h1 id="m-emp-titulo" className="m-titulo-pagina">Empleados</h1>

      <div className="m-emp-barra">
        <form className="m-buscador" role="search" onSubmit={e => { e.preventDefault(); e.currentTarget.querySelector('input')?.blur(); }}>
          <label htmlFor="m-emp-buscar" className="m-sr-only">Buscar empleado activo</label>
          <i className="ti ti-search" aria-hidden="true"></i>
          <input
            id="m-emp-buscar"
            type="search"
            placeholder="Buscar por nombre o # empleado"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
          {busqueda && (
            <button type="button" className="m-buscador-limpiar" aria-label="Limpiar búsqueda" onClick={() => setBusqueda('')}>
              <i className="ti ti-x" aria-hidden="true"></i>
            </button>
          )}
        </form>
        {!cargando && (
          <p className="m-emp-contador" aria-live="polite">
            {hayBusqueda ? `${lista.length} de ${activosTexto}` : activosTexto}
          </p>
        )}
      </div>

      {cargando ? (
        <div role="status" aria-label="Cargando empleados">
          <ul className="m-emp-lista" aria-hidden="true">
            {[0, 1, 2, 3].map(i => <li key={i} className="m-emp-esqueleto"><span></span><span></span></li>)}
          </ul>
        </div>
      ) : lista.length === 0 ? (
        <div className="m-emp-vacio" role="status">
          <i className={`ti ${hayBusqueda ? 'ti-search-off' : 'ti-users'}`} aria-hidden="true"></i>
          {hayBusqueda ? (
            <>
              <p className="m-emp-vacio-titulo">No se encontraron empleados activos.</p>
              <p className="m-emp-vacio-texto">Revisa el nombre o busca por # de empleado.</p>
              <div className="m-emp-vacio-acciones">
                <button type="button" className="m-btn m-btn-secundario" onClick={() => setBusqueda('')}>
                  Limpiar búsqueda
                </button>
              </div>
            </>
          ) : (
            <p className="m-emp-vacio-titulo">No hay empleados activos.</p>
          )}
        </div>
      ) : (
        <ul className="m-emp-lista" aria-label={activosTexto}>
          {lista.map(e => (
            <li key={String(e.id)}>
              <TarjetaEmpleado empleado={e} onAbrir={onAbrir} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
